import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Card, CardContent, CircularProgress, Grid, IconButton, MenuItem, Rating, TextField, Tooltip, Typography } from '@mui/material';
import RateReviewIcon from '@mui/icons-material/RateReview';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import EmailIcon from '@mui/icons-material/Email';
import { SubHeader } from '../../../utils/componentes-utiles/SubHeader';
import { getGestionEncuestasSatisfaccion } from '../../../services/apis';
import { ordenarOpcionesPorLabel, textoMayusculas } from '../../../utils/utils';

const filtrosVacios = {
    id_trabajador: '',
    id_cliente: '',
    fecha_inicio: '',
    fecha_fin: ''
};

export const GestionEncuestas = () => {
    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [encuestas, setEncuestas] = useState([]);
    const [trabajadores, setTrabajadores] = useState([]);
    const [clientes, setClientes] = useState([]);
    const [filtros, setFiltros] = useState(filtrosVacios);

    const cargarEncuestas = useCallback(async (filtrosConsulta = filtrosVacios) => {
        setLoaded(false);
        setError('');

        const response = await getGestionEncuestasSatisfaccion(filtrosConsulta);

        if (response?.success) {
            const data = response.data || {};

            setEncuestas(Array.isArray(data.encuestas) ? data.encuestas : []);
            setTrabajadores(ordenarOpcionesPorLabel(data.trabajadores));
            setClientes(ordenarOpcionesPorLabel(data.clientes));
        } else {
            setError(response?.message || 'No se han podido cargar las encuestas de satisfaccion.');
        }

        setLoaded(true);
    }, []);

    useEffect(() => {
        cargarEncuestas();
    }, [cargarEncuestas]);

    const handleFiltro = (event) => {
        const { name, value } = event.target;
        const nuevosFiltros = { ...filtros, [name]: value };
        setFiltros(nuevosFiltros);
        cargarEncuestas(nuevosFiltros);
    };

    const limpiarFiltros = () => {
        const filtrosLimpios = filtrosVacios;
        setFiltros(filtrosLimpios);
        cargarEncuestas(filtrosLimpios);
    };

    const formatearFecha = (fecha) => {
        if (!fecha) return 'Sin fecha';

        const fechaObj = new Date(fecha);
        if (Number.isNaN(fechaObj.getTime())) {
            return fecha;
        }

        return fechaObj.toLocaleDateString('es-ES');
    };

    return (
        <div className='menu-back fade-in2'>
            <SubHeader titulo={'Gestion de encuestas'} />

            <Grid container item xs={12} justifyContent="center" sx={{ px: { xs: 1, md: 3 }, pb: 3 }}>
                <Grid item xs={12} lg={11}>
                    <Card sx={{ background: 'var(--fondoTarjeta)', borderRadius: 2, mb: 2 }}>
                        <CardContent>
                            <Grid container spacing={2} alignItems="center">
                                <Grid item xs={12} md={3}>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Trabajador"
                                        name="id_trabajador"
                                        value={filtros.id_trabajador}
                                        onChange={handleFiltro}
                                    >
                                        <MenuItem value="">Todos</MenuItem>
                                        {trabajadores.map((trabajador) => (
                                            <MenuItem key={trabajador.value} value={trabajador.value}>
                                                {textoMayusculas(trabajador.label)}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>

                                <Grid item xs={12} md={3}>
                                    <TextField
                                        select
                                        fullWidth
                                        size="small"
                                        label="Cliente"
                                        name="id_cliente"
                                        value={filtros.id_cliente}
                                        onChange={handleFiltro}
                                    >
                                        <MenuItem value="">Todos</MenuItem>
                                        {clientes.map((cliente) => (
                                            <MenuItem key={cliente.value} value={cliente.value}>
                                                {textoMayusculas(cliente.label)}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                </Grid>

                                <Grid item xs={6} md={2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="date"
                                        label="Desde"
                                        name="fecha_inicio"
                                        value={filtros.fecha_inicio}
                                        onChange={handleFiltro}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                <Grid item xs={6} md={2}>
                                    <TextField
                                        fullWidth
                                        size="small"
                                        type="date"
                                        label="Hasta"
                                        name="fecha_fin"
                                        value={filtros.fecha_fin}
                                        onChange={handleFiltro}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </Grid>

                                <Grid item xs={12} md={2}>
                                    <Button
                                        fullWidth
                                        variant="outlined"
                                        color="primary"
                                        startIcon={<FilterAltOffIcon />}
                                        onClick={limpiarFiltros}
                                    >
                                        Limpiar
                                    </Button>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error}
                        </Alert>
                    )}

                    {!loaded && (
                        <Grid container justifyContent="center" sx={{ mt: 3 }}>
                            <CircularProgress />
                        </Grid>
                    )}

                    {loaded && !error && (
                        <>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                Encuestas recibidas: {encuestas.length}
                            </Typography>

                            <Grid container spacing={1.5}>
                                {encuestas.length === 0 && (
                                    <Grid item xs={12}>
                                        <Alert severity="info">No hay encuestas de satisfaccion con los filtros seleccionados.</Alert>
                                    </Grid>
                                )}

                                {encuestas.map((encuesta) => (
                                    <Grid item xs={12} key={encuesta.id}>
                                        <Card sx={{ background: 'var(--fondoTarjeta)', borderRadius: 2 }}>
                                            <CardContent sx={{ py: 1.5, px: { xs: 2, md: 3 }, '&:last-child': { pb: 1.5 } }}>
                                                <Box
                                                    sx={{
                                                        display: 'grid',
                                                        gridTemplateColumns: { xs: '1fr', md: 'repeat(5, minmax(0, 1fr)) 48px' },
                                                        gap: { xs: 1.5, md: 3 },
                                                        alignItems: 'center',
                                                        width: '100%'
                                                    }}
                                                >
                                                    <Box textAlign="center">
                                                        <Box display="flex" justifyContent="center" alignItems="center" gap={0.5}>
                                                            <RateReviewIcon color="primary" fontSize="small" />
                                                            <Typography variant="caption" color="text.secondary">
                                                                Trabajador
                                                            </Typography>
                                                        </Box>
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {encuesta.nombre_trabajador || 'Sin trabajador'}
                                                        </Typography>
                                                    </Box>

                                                    <Box textAlign="center">
                                                        <Typography variant="caption" color="text.secondary">
                                                            Cliente
                                                        </Typography>
                                                        <Typography variant="body2" fontWeight={600}>
                                                            {encuesta.nombre_cliente || 'Sin cliente'}
                                                        </Typography>
                                                        {encuesta.nombre_usuario_cliente && (
                                                            <Typography variant="caption" color="text.secondary" display="block">
                                                                {encuesta.nombre_usuario_cliente}
                                                            </Typography>
                                                        )}
                                                    </Box>

                                                    <Box textAlign="center">
                                                        <Typography variant="caption" color="text.secondary">
                                                            Puntuacion
                                                        </Typography>
                                                        <Box display="flex" justifyContent="center" alignItems="center" gap={1}>
                                                            <Rating value={Number(encuesta.puntuacion) || 0} readOnly size="small" />
                                                            <Typography variant="body2">{encuesta.puntuacion}/5</Typography>
                                                        </Box>
                                                    </Box>

                                                    <Box textAlign="center">
                                                        <Typography variant="caption" color="text.secondary">
                                                            Fecha
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {formatearFecha(encuesta.fecha)}
                                                        </Typography>
                                                    </Box>

                                                    <Box textAlign="center">
                                                        <Typography variant="caption" color="text.secondary">
                                                            Comentario
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            {encuesta.comentario || 'Sin comentario'}
                                                        </Typography>
                                                    </Box>

                                                    <Box textAlign="center">
                                                        {encuesta.mail_usuario_cliente && (
                                                            <Tooltip title={`Enviar correo a ${encuesta.mail_usuario_cliente}`}>
                                                                <IconButton
                                                                    color="primary"
                                                                    size="small"
                                                                    component="a"
                                                                    href={`mailto:${encuesta.mail_usuario_cliente}`}
                                                                >
                                                                    <EmailIcon fontSize="small" />
                                                                </IconButton>
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                ))}
                            </Grid>
                        </>
                    )}
                </Grid>
            </Grid>
        </div>
    );
};
